// Provably-fair derivations + client-side verifier (WebCrypto).
//
// Byte-exact mirror of og-games-api app/fairness/rng.py and
// app/games/horseracing/race.py. Three deterministic streams:
//
//   FIELD      Generator(sha256(serverSeed), 'horseracing-field:v1', nonce)
//              Keyed by the PUBLIC seed hash, so the whole tote board -
//              runners, form weights, every price - is derived in this
//              browser BEFORE a bet is accepted. The house commits to the
//              hash long before the round; the field cannot be steered.
//
//   ORDER      the round's main stream (serverSeed, clientSeed, nonce):
//              one Plackett-Luce draw, exactly n-1 floats.
//
//   TELEMETRY  Generator(serverSeed, `${clientSeed}:horseracing-telemetry:v1`,
//              nonce): finish gaps + per-horse pace params, domain-separated
//              so presentation upgrades never disturb old outcomes.
//
// Physical appearance is attached from stable horse identity after the FIELD
// draws. It consumes no stream bytes and is intentionally outside the money /
// outcome verdict, while still travelling on the snapshotted field record.
//
// Every float here is exact in IEEE-754 (u32/2^32 products against small
// ints), so this verifier reproduces the server's numbers bit for bit.

import { normalizeHorseAppearance } from './horse-appearance.js';
import {
  DISTANCE_STEP_METERS,
  LAP_DISTANCE_METERS,
  validateDistanceMeters,
} from './race-distance.js';

export const FIELD_DOMAIN = 'horseracing-field:v1';
export const TELEMETRY_DOMAIN = 'horseracing-telemetry:v1';

const WEIGHT_MIN = 10;
const WEIGHT_MAX = 40;

// Telemetry draw constants - part of the physics v1 spec (race.py).
const WINNER_JITTER = 0.08;
const GAP_MIN_S = 0.06;
const GAP_SPAN_S = 1.0;
const PACE_MIN = 0.86;
const PACE_SPAN = 0.28;
const TRACK_LENGTH = 600.0;
const GATE_INTRO_MS = 3000;
const FINISH_HOLD_MS = 4000;

const enc = new TextEncoder();

export async function sha256Hex(text) {
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function hmacKey(keyText) {
  return crypto.subtle.importKey(
    'raw', enc.encode(keyText), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
  );
}

/**
 * The platform RNG: HMAC-SHA256 block stream over (serverSeed, clientSeed,
 * nonce) with a global byte cursor - block(i) = HMAC(serverSeed,
 * `${clientSeed}:${nonce}:${i}`). Async because WebCrypto is; the draw
 * ORDER is what verification replays, so callers await sequentially.
 */
export class StreamGenerator {
  constructor(serverSeed, clientSeed, nonce) {
    this.serverSeed = serverSeed;
    this.clientSeed = clientSeed;
    this.nonce = String(nonce);
    this.cursor = 0;
    this._key = null;
    this._blocks = new Map();
  }

  async _block(index) {
    if (this._blocks.has(index)) return this._blocks.get(index);
    if (!this._key) this._key = await hmacKey(this.serverSeed);
    const sig = await crypto.subtle.sign(
      'HMAC', this._key, enc.encode(`${this.clientSeed}:${this.nonce}:${index}`),
    );
    const bytes = new Uint8Array(sig);
    this._blocks.set(index, bytes);
    return bytes;
  }

  async read(count) {
    const out = new Uint8Array(count);
    let filled = 0;
    let blockIndex = Math.floor(this.cursor / 32);
    let offset = this.cursor % 32;
    while (filled < count) {
      const digest = await this._block(blockIndex);
      const take = digest.subarray(offset, offset + (count - filled));
      out.set(take, filled);
      filled += take.length;
      offset = 0;
      blockIndex += 1;
    }
    this.cursor += count;
    return out;
  }

  async generateFloat() {
    const b = await this.read(4);
    const u = ((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0;
    return u / 4294967296;
  }

  async generateInt(lo, hi) {
    if (lo >= hi) return lo;
    const f = await this.generateFloat();
    return lo + Math.floor(f * (hi - lo + 1));
  }
}

// ------------------------------------------------------------------ field --
/**
 * The runners for one race, derived from PUBLIC data (seed hash + nonce).
 * `pools` carries server-published name/silk/bloodline/appearance tables from
 * game meta. Appearance is aligned to the stable name pool; a missing legacy
 * entry receives the identity fallback without consuming another RNG draw.
 */
export async function deriveField(serverSeedHashed, nonce, fieldSize, pools) {
  const gen = new StreamGenerator(serverSeedHashed, FIELD_DOMAIN, nonce);
  const namePool = [...pools.names.keys()];
  const silkPool = [...pools.silks.keys()];
  const horses = [];
  for (let gate = 0; gate < fieldSize; gate++) {
    const weight = await gen.generateInt(WEIGHT_MIN, WEIGHT_MAX);
    const nameI = namePool.splice(await gen.generateInt(0, namePool.length - 1), 1)[0];
    const silkI = silkPool.splice(await gen.generateInt(0, silkPool.length - 1), 1)[0];
    const bloodline = await gen.generateInt(0, pools.bloodlines.length - 1);
    const name = pools.names[nameI];
    horses.push({
      gate,
      name,
      weight,
      silk: pools.silks[silkI],
      bloodline,
      appearance: normalizeHorseAppearance(pools.appearances?.[nameI], name),
    });
  }
  return { horses };
}

// ------------------------------------------------------------------ order --
/** One Plackett-Luce draw of the finish order: exactly n-1 floats. */
export async function sampleOrder(gen, weights) {
  const remaining = [...weights.keys()];
  let total = weights.reduce((a, b) => a + b, 0);
  const order = [];
  while (remaining.length > 1) {
    const x = (await gen.generateFloat()) * total;
    let acc = 0;
    let pick = remaining[remaining.length - 1];
    for (const h of remaining) {
      acc += weights[h];
      if (x < acc) { pick = h; break; }
    }
    order.push(pick);
    remaining.splice(remaining.indexOf(pick), 1);
    total -= weights[pick];
  }
  order.push(remaining[0]);
  return order;
}

// -------------------------------------------------------------- telemetry --
/** The envelope fitted to a drawn order (verification replay). */
export async function deriveTelemetry(
  serverSeed,
  clientSeed,
  nonce,
  order,
  raceDurationMs,
  distanceMeters = null,
  lapDistanceMeters = LAP_DISTANCE_METERS,
) {
  const tgen = new StreamGenerator(
    serverSeed, `${clientSeed}:${TELEMETRY_DOMAIN}`, nonce,
  );
  const n = order.length;
  const durationS = raceDurationMs / 1000;
  const winnerS = durationS * (1 - WINNER_JITTER / 2 + WINNER_JITTER * await tgen.generateFloat());
  const finishByPos = [winnerS];
  for (let k = 1; k < n; k++) {
    const f = await tgen.generateFloat();
    finishByPos.push(finishByPos[k - 1] + GAP_MIN_S + GAP_SPAN_S * f * f);
  }
  const finishByHorse = new Array(n).fill(0);
  order.forEach((horse, pos) => { finishByHorse[horse] = finishByPos[pos]; });

  const horses = [];
  for (let i = 0; i < n; i++) {
    const seed = await tgen.generateInt(0, 0xFFFFFFFF);
    const early = PACE_MIN + PACE_SPAN * await tgen.generateFloat();
    const kick = PACE_MIN + PACE_SPAN * await tgen.generateFloat();
    const lane = await tgen.generateFloat();
    horses.push({ seed, early, kick, lane, finishTime: finishByHorse[i] });
  }
  const envelope = {
    version: 1,
    trackLength: distanceMeters == null
      ? TRACK_LENGTH
      : Number(validateDistanceMeters(Number(distanceMeters))),
    introMs: GATE_INTRO_MS,
    holdMs: FINISH_HOLD_MS,
    raceDurationMs,
    horses,
  };
  if (distanceMeters != null) {
    envelope.distanceMeters = Number(distanceMeters);
    envelope.lapDistanceMeters = Number(lapDistanceMeters);
    envelope.markerIntervalMeters = DISTANCE_STEP_METERS;
    envelope.lapCount = Math.ceil(
      envelope.distanceMeters / envelope.lapDistanceMeters,
    );
  }
  return envelope;
}

// ------------------------------------------------------------ verification --
function sameField(a, b) {
  if (!a || !b || a.horses.length !== b.horses.length) return false;
  return a.horses.every((h, i) => {
    const o = b.horses[i];
    // Appearance is presentation metadata. A changed/missing legacy value
    // must not alter the provably-fair verdict for weights/order/settlement.
    return h.name === o.name && h.weight === o.weight
      && h.silk === o.silk && h.bloodline === o.bloodline;
  });
}

function sameEnvelope(a, b) {
  if (!a || !b || a.horses.length !== b.horses.length) return false;
  const scalarKeys = [
    'version', 'trackLength', 'introMs', 'holdMs', 'raceDurationMs',
    'distanceMeters', 'lapDistanceMeters', 'markerIntervalMeters', 'lapCount',
  ];
  if (!scalarKeys.every((key) => (
    (a[key] == null && b[key] == null) || a[key] === b[key]
  ))) return false;
  return a.horses.every((h, i) => {
    const o = b.horses[i];
    return h.seed === o.seed
      && Math.abs(h.early - o.early) < 1e-12
      && Math.abs(h.kick - o.kick) < 1e-12
      && Math.abs(h.lane - o.lane) < 1e-12
      && Math.abs(h.finishTime - o.finishTime) < 1e-9;
  });
}

/**
 * Verify a finished race:
 *   { serverSeed, serverSeedHash, clientSeed, nonce, order, field,
 *     physics, fieldSize, raceDurationMs }
 * Single player passes the player's seed pair; multiplayer passes the
 * TABLE seed + combinedClientSeed. `ok` stays null (pending) while the
 * server seed is still committed (reveals on seed rotation).
 */
export async function verifyRace(proof) {
  if (proof.serverSeed == null) {
    return { ok: null, pending: true, hashOk: null, fieldOk: null, orderOk: null, physicsOk: null };
  }
  try {
    const hashOk = (await sha256Hex(proof.serverSeed)) === proof.serverSeedHash;
    const fieldSize = proof.fieldSize || (proof.field ? proof.field.horses.length : 8);
    const derivedField = await deriveField(
      await sha256Hex(proof.serverSeed), proof.nonce, fieldSize, proof.pools,
    );
    const fieldOk = proof.field ? sameField(derivedField, proof.field) : null;
    const gen = new StreamGenerator(proof.serverSeed, proof.clientSeed, proof.nonce);
    const weights = derivedField.horses.map((h) => h.weight);
    const derivedOrder = await sampleOrder(gen, weights);
    const orderOk = JSON.stringify(derivedOrder) === JSON.stringify(proof.order);
    let physicsOk = null;
    if (proof.physics) {
      const env = await deriveTelemetry(
        proof.serverSeed, proof.clientSeed, proof.nonce, derivedOrder,
        proof.physics.raceDurationMs || proof.raceDurationMs || 30000,
        proof.physics.distanceMeters ?? null,
        proof.physics.lapDistanceMeters
          ?? LAP_DISTANCE_METERS,
      );
      physicsOk = sameEnvelope(env, proof.physics);
    }
    return {
      ok: hashOk && fieldOk !== false && orderOk && physicsOk !== false,
      hashOk,
      fieldOk,
      orderOk,
      physicsOk,
      derivedOrder,
    };
  } catch (e) {
    return { ok: false, hashOk: false, fieldOk: false, orderOk: false, physicsOk: false };
  }
}
